import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { ProductType } from '~/pages/pay/api/getCatalog/getCatalog.schema';
import { SnackBar } from '~/shared/ui/SnackBar/SnackBar';
import { Typography } from '~/shared/ui/Typography/Typography';

import { useLocalization } from '../../../localization/localization';
import { FormType } from '../../PayPage.scheme';

import { Root, List, ButtonItem, MoreButton } from './Products.ui';

const COLLAPSED_ROWS_COUNT = 6;

export const Products = ({
  isZeroPrice,
  isPricesError,
  products,
}: {
  isZeroPrice?: boolean;
  isPricesError?: boolean;
  products?: ProductType[];
}) => {
  const [errorType, setErrorType] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const { setValue, watch } = useFormContext<FormType>();
  const currentProduct = watch('product');
  const { t } = useLocalization();

  const isError = isPricesError || isZeroPrice;

  useEffect(() => {
    if (isZeroPrice) {
      setErrorType('zeroPrice');
    }
    if (isPricesError) {
      setErrorType('serverError');
    }
  }, [isPricesError, isZeroPrice]);

  useEffect(() => {
    if (!isError) {
      setErrorType(null);
    }
  }, [isError]);

  const hasMore = products && products.length > COLLAPSED_ROWS_COUNT;

  const handleProductClick = (product: ProductType) => {
    setValue('product', product, { shouldValidate: true });
  };

  return (
    <Root>
      <SnackBar
        title={
          errorType === 'zeroPrice' ? t('zero price error') : t('price error')
        }
        onClose={() => setErrorType(null)}
        open={!!errorType}
      />
      <List expanded={expanded} totalItems={products?.length || 0}>
        {products?.map((product) => (
          <ButtonItem
            key={product.id}
            active={currentProduct?.id === product.id}
            onClick={() => handleProductClick(product)}
            type="button"
            tabIndex={0}
          >
            <Typography.Paragraph>{product?.info?.label}</Typography.Paragraph>
          </ButtonItem>
        ))}
      </List>

      {hasMore && (
        <MoreButton
          onClick={() => setExpanded((e) => !e)}
          type="button"
          isExpanded={expanded}
        >
          {expanded ? t('Collapse') : t('More')}
        </MoreButton>
      )}
    </Root>
  );
};
